import PropTypes from 'prop-types';
import React from 'react';

import Button from 'components/Button';
import Image from 'components/ImageV2';
import { IMAGE_RATIOS } from 'constants/images';
import I18n from 'utils/I18n';

import 'styles/tailwind.css';

const StoreDetailPage = ({ storeLocation }) => {
  const {
    city,
    countryRegion,
    featuredImage,
    googleMapsUrl,
    phoneNumber,
    servicesV2,
    storeDescription,
    stateProvince,
    storeHours,
    storeName,
    streetAddress,
    zipPostalCode,
  } = storeLocation;

  return (
    <article className="flex-col p-0 lg:container lg:mx-auto lg:flex lg:px-4 lg:pt-16">
      <div className="order-2 mb-4 aspect-video overflow-hidden">
        <Image
          src={featuredImage?.file?.url}
          alt={storeName}
          width={2400}
          ratio={IMAGE_RATIOS.R_16x9}
          className="block object-cover object-center"
        />
      </div>

      <div className="order-1 mb-8 px-4 lg:mb-16 lg:p-0">
        <nav aria-label={I18n.localize('nav.breadcrumb')} className="breadcrumbs mb-4 text-sm">
          <ol className="flex">
            <li className="after:mx-1 after:content-['/']">
              <a className="underline" href="/pages/stores">
                {I18n.localize('storeLocator.stores')}
              </a>
            </li>
            <li aria-current>{storeName}</li>
          </ol>
        </nav>
        <div className="lg:grid lg:grid-cols-12 lg:gap-8">
          <div className="col-span-6 mb-6 lg:mb-0">
            <div className="md:mr-12">
              <div className="mb-4">
                <h1 className="font-bold">
                  {storeName?.split?.(' - ')?.map?.((name, index) => {
                    return index === 0 ? (
                      <span key={name} className="block text-3xl md:text-4xl">
                        {name}
                      </span>
                    ) : (
                      <span key={name} className="text-2xl text-black-400 md:text-3xl">
                        {name}
                      </span>
                    );
                  })}
                </h1>
              </div>

              <p className="mb-4">{storeDescription}</p>
              <div className="flex gap-1">
                {servicesV2?.map(service => (
                  <p
                    className="bg-gray-500 px-3 py-2 text-center text-xs font-medium text-white"
                    key={service.label}
                  >
                    {service.label}
                  </p>
                ))}
              </div>
            </div>
          </div>

          <div className="col-span-3">
            <div className="flex-col justify-between lg:flex lg:h-full">
              <div className="mb-6 lg:mb-0">
                <h2 className="mb-2 font-bold capitalize">{I18n.localize('app.hours')}:</h2>
                {storeHours.map(hour => (
                  <p className="leading-6" key={hour}>
                    {hour}
                  </p>
                ))}
              </div>
              <div className="mb-6 lg:mb-0">
                <h2 className="mb-2 font-bold capitalize">
                  {I18n.localize('storeLocator.contact')}:
                </h2>
                <a className="underline" href={`tel: ${phoneNumber}`}>
                  {phoneNumber}
                </a>
              </div>
            </div>
          </div>

          <div className="col-span-3">
            <div className="flex-col justify-between lg:flex lg:h-full">
              <div className="mb-6 lg:mb-0">
                <h2 className="mb-2 font-bold capitalize">
                  {I18n.localize('storeLocator.address')}:
                </h2>
                <p className="leading-6">{streetAddress}</p>
                <p className="leading-6">
                  {city}, {stateProvince} {zipPostalCode}
                </p>
                <p className="leading-6">{countryRegion}</p>
              </div>
              <Button linkTo={googleMapsUrl} target="_blank" isFullWidth>
                {I18n.localize('storeLocator.directions')}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </article>
  );
};

StoreDetailPage.propTypes = {
  storeLocation: PropTypes.shape({
    city: PropTypes.string,
    countryRegion: PropTypes.string,
    featuredImage: PropTypes.shape({
      file: PropTypes.shape({
        url: PropTypes.string,
      }),
    }),
    googleMapsUrl: PropTypes.string,
    phoneNumber: PropTypes.string,
    servicesV2: PropTypes.arrayOf(PropTypes.shape({})),
    storeDescription: PropTypes.string,
    storeHours: PropTypes.arrayOf(PropTypes.string),
    storeName: PropTypes.string,
    stateProvince: PropTypes.string,
    streetAddress: PropTypes.string,
    zipPostalCode: PropTypes.string,
  }),
};

export default StoreDetailPage;
